/**
 * Command palette (item #20 Phase 2 - multi-scope).
 *
 * Single Cmd-K entry, multiple scopes via prefix:
 *   no prefix  -> Global search (messages + deals + parties)
 *   #          -> Jump to deal (search deal_threads)
 *   @          -> Jump to party (search deal_parties)
 *   >          -> Run action / skill (Neo NL dispatch placeholder; wired in Phase 4)
 *   :          -> Open saved view
 *
 * Scope is detected from the first character of the query. The remainder is
 * the search term passed to the appropriate backend endpoint. Empty palette
 * shows nav shortcuts.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Command } from 'cmdk';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox,
  FolderOpen,
  Bot,
  Search,
  MessageSquare,
  FileText,
  CheckSquare,
  Maximize2,
  Minimize2,
  ArrowRight,
  Hash,
  AtSign,
  ChevronsRight,
  BookmarkPlus,
  Sparkles,
} from 'lucide-react';
import { api, queryKeys } from '../../lib/api.js';
import type { AiEmployee, Note, Task } from '../../../../shared/schema.js';
import { dealCurrentStageLabel, dealDisplayTitle, dealTypeDisplayLabel, type DealListDeal } from '../../lib/deal-list-display.js';
import {
  buildMessageSearchPath,
  formatMessageSearchResult,
  messageSearchHref,
  type MessageSearchItem,
} from '../../lib/messageSearch.js';

type Scope = 'global' | 'deal' | 'party' | 'action' | 'view';
type ObjectType = 'all' | 'deal' | 'party' | 'contact' | 'message' | 'note' | 'task' | 'ai-employee';

interface PartySearchHit {
  id: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
}

interface ScopeHint {
  scope: Scope;
  prefix: string;
  label: string;
  description: string;
  icon: typeof Hash;
}

const SCOPES: ScopeHint[] = [
  { scope: 'deal',   prefix: '#', label: 'Jump to deal',   description: 'Search deal titles', icon: Hash },
  { scope: 'party',  prefix: '@', label: 'Jump to party',  description: 'Search party names + emails', icon: AtSign },
  { scope: 'action', prefix: '>', label: 'Run action',     description: 'Dispatch a Neo skill in plain English', icon: ChevronsRight },
  { scope: 'view',   prefix: ':', label: 'Open saved view', description: 'Apply a saved filter view', icon: BookmarkPlus },
];

const OBJECT_TYPES: Array<{ value: ObjectType; label: string; icon: typeof Search }> = [
  { value: 'all', label: 'All', icon: Search },
  { value: 'deal', label: 'Deal', icon: FolderOpen },
  { value: 'party', label: 'Party', icon: AtSign },
  { value: 'contact', label: 'Contact', icon: AtSign },
  { value: 'message', label: 'Message', icon: MessageSquare },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'task', label: 'Task', icon: CheckSquare },
  { value: 'ai-employee', label: 'AI employee', icon: Bot },
];

export const COMMAND_NAV_ITEMS = [
  { label: 'Inbox', href: '/inbox', icon: Inbox, shortcut: 'g i' },
  { label: 'Deals', href: '/deals', icon: FolderOpen, shortcut: 'g d' },
];

interface CmdPaletteProps {
  open: boolean;
  onClose: () => void;
}

function detectScope(input: string): { scope: Scope; term: string } {
  if (input.length === 0) return { scope: 'global', term: '' };
  const first = input[0]!;
  const rest = input.slice(1).trimStart();
  if (first === '#') return { scope: 'deal', term: rest };
  if (first === '@') return { scope: 'party', term: rest };
  if (first === '>') return { scope: 'action', term: rest };
  if (first === ':') return { scope: 'view', term: rest };
  return { scope: 'global', term: input };
}

const SCOPE_LABEL: Record<Scope, string> = {
  global: 'Global search',
  deal:   '# Jump to deal',
  party:  '@ Jump to party',
  action: '> Run action',
  view:   ': Open saved view',
};

const SCOPE_COLOR: Record<Scope, string> = {
  global: 'text-ink-muted',
  deal:   'text-info',
  party:  'text-chart-4',
  action: 'text-primary',
  view:   'text-warning',
};

function allowsObjectType(active: ObjectType, type: ObjectType) {
  return active === 'all' || active === type;
}

function includesTerm(value: unknown, term: string) {
  return typeof value === 'string' && value.toLowerCase().includes(term.toLowerCase());
}

function noteTitle(note: Note) {
  return note.body.trim().split('\n')[0]?.slice(0, 90) || 'Untitled note';
}

function taskSubtitle(task: Task) {
  return [task.status.replace(/_/g, ' '), task.priority].filter(Boolean).join(' / ');
}

function dealStageSubtitle(deal: DealListDeal) {
  return [dealCurrentStageLabel(deal), dealTypeDisplayLabel(deal)]
    .filter(Boolean)
    .join(' / ');
}

export default function CmdPalette({ open, onClose }: CmdPaletteProps) {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState('');
  const [objectType, setObjectType] = useState<ObjectType>('all');
  const [expanded, setExpanded] = useState(false);

  const { scope, term } = useMemo(() => detectScope(query), [query]);
  const termLower = term.toLowerCase();

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Reset query on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setObjectType('all');
      setExpanded(false);
    }
  }, [open]);

  // Search deals when scope = deal | global, term >= 2 chars
  const dealsSearchEnabled = open && term.length >= 2 && (scope === 'deal' || scope === 'global') && allowsObjectType(objectType, 'deal');
  const { data: dealsData } = useQuery({
    queryKey: queryKeys.deals.list({ q: term, scope }),
    queryFn: () => api.get<{ items: DealListDeal[] }>(`/api/deals?q=${encodeURIComponent(term)}&limit=5`),
    enabled: dealsSearchEnabled,
    staleTime: 0,
  });
  const deals = dealsData?.items || [];

  // Search message queue when scope = global, term >= 2 chars
  const messagesSearchEnabled = open && term.length >= 2 && scope === 'global' && allowsObjectType(objectType, 'message');
  const { data: messagesData } = useQuery({
    queryKey: ['messages-search', term],
    queryFn: () => api.get<{ items: MessageSearchItem[] }>(buildMessageSearchPath(term, 5)),
    enabled: messagesSearchEnabled,
    staleTime: 0,
  });
  const messages = messagesData?.items || [];

  // Search party master when scope = party | global, term >= 2 chars
  const partiesSearchEnabled = open && term.length >= 2 && (scope === 'party' || scope === 'global') && (allowsObjectType(objectType, 'party') || allowsObjectType(objectType, 'contact'));
  const { data: partiesData } = useQuery({
    queryKey: ['parties-search', term],
    queryFn: () => api.get<{ parties: PartySearchHit[] }>(`/api/parties/search?q=${encodeURIComponent(term)}&limit=5`),
    enabled: partiesSearchEnabled,
    staleTime: 0,
  });
  const parties = partiesData?.parties || [];

  const notesSearchEnabled = open && term.length >= 2 && scope === 'global' && allowsObjectType(objectType, 'note');
  const { data: notesData } = useQuery({
    queryKey: ['notes-search', term],
    queryFn: () => api.get<{ notes: Note[] }>('/api/notes'),
    enabled: notesSearchEnabled,
    staleTime: 30_000,
  });
  const notes = useMemo(() => (notesData?.notes ?? [])
    .filter((note) => includesTerm(note.body, termLower))
    .slice(0, 5), [notesData?.notes, termLower]);

  const tasksSearchEnabled = open && term.length >= 2 && scope === 'global' && allowsObjectType(objectType, 'task');
  const { data: tasksData } = useQuery({
    queryKey: ['tasks-search', term],
    queryFn: () => api.get<{ tasks: Task[] }>('/api/tasks?includeDone=1&assignee=team'),
    enabled: tasksSearchEnabled,
    staleTime: 30_000,
  });
  const tasks = useMemo(() => (tasksData?.tasks ?? [])
    .filter((task) => includesTerm(task.title, termLower) || includesTerm(task.description, termLower))
    .slice(0, 5), [tasksData?.tasks, termLower]);

  const employeesSearchEnabled = open && term.length >= 2 && scope === 'global' && allowsObjectType(objectType, 'ai-employee');
  const { data: employeesData } = useQuery({
    queryKey: ['ai-employees-search', term],
    queryFn: () => api.get<{ employees: AiEmployee[] }>('/api/ai-employees'),
    enabled: employeesSearchEnabled,
    staleTime: 30_000,
  });
  const employees = useMemo(() => (employeesData?.employees ?? [])
    .filter((employee) => includesTerm(employee.name, termLower) || includesTerm(employee.description, termLower))
    .slice(0, 5), [employeesData?.employees, termLower]);

  const go = (href: string) => {
    navigate(href);
    onClose();
  };

  // Dispatch a natural-language command via /api/neo/dispatch-natural-language.
  // Keeps the palette open while the request is in flight, replaces the
  // results with a single line showing the outcome, then auto-closes after a
  // short delay so the operator sees what was matched.
  const [dispatching, setDispatching] = useState<null | {
    state: 'pending' | 'done' | 'error';
    label?: string;
    source?: 'live' | 'stub';
  }>(null);
  const dispatchNlAction = (text: string, close: () => void) => {
    setDispatching({ state: 'pending' });
    void api
      .post<{ result: { skillId: string; status: string; proposalId?: string; error?: string }; source: 'live' | 'stub' }>(
        '/api/neo/dispatch-natural-language',
        { text },
      )
      .then((res) => {
        const r = res.result;
        if (r.status === 'failed' || r.skillId === 'unknown') {
          setDispatching({ state: 'error', label: r.error ?? 'No skill matched', source: res.source });
        } else {
          const verb = r.status === 'proposed' ? 'queued for approval' : r.status;
          setDispatching({ state: 'done', label: `${r.skillId.replace(/-/g, ' ')} ${verb}`, source: res.source });
        }
        setTimeout(() => { setDispatching(null); close(); }, 1400);
      })
      .catch(() => {
        setDispatching({ state: 'error', label: 'Dispatch failed - check your connection' });
        setTimeout(() => { setDispatching(null); close(); }, 1800);
      });
  };

  // Reset dispatch banner when palette reopens.
  useEffect(() => { if (!open) setDispatching(null); }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-modal-overlay"
            onClick={onClose}
          />

          {/* Palette - centered on the viewport (it is a global overlay over a
              full-viewport scrim; the prior workspace-offset math pushed it
              right of center because the left nav/context offset had no
              balancing right offset). */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, x: '-50%', y: -8 }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: 0 }}
            exit={{ opacity: 0, scale: 0.96, x: '-50%', y: -8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={[
              'fixed left-1/2 w-[calc(100vw-2rem)] sm:w-[calc(100vw-1.5rem)] bg-surface-raised rounded-xl shadow-2xl border border-line overflow-hidden z-command-palette',
              expanded
                ? 'top-[9vh] sm:top-[5vh] h-[82vh] sm:h-[90vh] max-w-6xl'
                : 'top-[22vh] sm:top-[18vh] max-w-xl',
            ].join(' ')}
          >
            <Command label="Command palette" shouldFilter={false} className="flex h-full flex-col">
              {/* Search input + scope badge */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
                <Search size={16} className="text-ink-subtle flex-shrink-0" />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search... or # deal, @ party, > action, : view"
                  className="flex-1 bg-transparent text-ink placeholder:text-ink-subtle text-base sm:text-sm outline-none"
                  autoFocus
                />
                {scope !== 'global' && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-sunken ${SCOPE_COLOR[scope]}`}>
                    {SCOPE_LABEL[scope]}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setExpanded((value) => !value)}
                  className="rounded-md p-1 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
                  aria-label={expanded ? 'Collapse command palette' : 'Expand command palette'}
                >
                  {expanded ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
                </button>
                <kbd className="text-[10px] text-ink-subtle bg-surface-sunken px-1.5 py-0.5 rounded">ESC</kbd>
              </div>
              <div className="border-b border-line bg-surface/80 px-3 py-2">
                <div className="flex gap-1 overflow-x-auto pb-0.5" aria-label="Object type filters">
                  {OBJECT_TYPES.map(({ value, label, icon: Icon }) => {
                    const selected = objectType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setObjectType(value)}
                        aria-pressed={selected}
                        className={[
                          'inline-flex h-7 flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold transition-colors',
                          selected
                            ? 'border-selected-border bg-selected text-selected-fg'
                            : 'border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink',
                        ].join(' ')}
                      >
                        <Icon size={12} aria-hidden />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Command.List className={['overflow-y-auto py-2', expanded ? 'flex-1' : 'max-h-96'].join(' ')}>
                {/* Empty palette - show scope hints + nav */}
                {!query && (
                  <>
                    <Command.Group heading="Scopes" className="px-2">
                      {SCOPES.map(({ scope: s, prefix, label, description, icon: Icon }) => (
                        <Command.Item
                          key={prefix}
                          value={`scope-${s}`}
                          onSelect={() => setQuery(prefix + ' ')}
                          className="
                            flex items-center gap-3 px-3 py-2 rounded-lg
                            text-sm text-ink cursor-pointer
                            data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                            hover:bg-surface-sunken transition-colors duration-75
                          "
                        >
                          <Icon size={15} className={`flex-shrink-0 ${SCOPE_COLOR[s]}`} />
                          <span className="flex-1 min-w-0 truncate">{label}</span>
                          <span className="hidden sm:inline text-xs text-ink-subtle truncate max-w-[200px]">{description}</span>
                          <kbd className="text-[10px] font-mono bg-surface-sunken px-1.5 py-0.5 rounded border border-line/60 text-ink-muted flex-shrink-0">{prefix}</kbd>
                        </Command.Item>
                      ))}
                    </Command.Group>
                    <div className="px-2 pt-1">
                      <Command.Group heading="Navigate" className="">
                        {COMMAND_NAV_ITEMS.map(({ label, href, icon: Icon, shortcut }) => (
                          <Command.Item
                            key={href}
                            value={href}
                            onSelect={() => go(href)}
                            className="
                              flex items-center gap-3 px-3 py-2 rounded-lg
                              text-sm text-ink cursor-pointer
                              data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                              hover:bg-surface-sunken transition-colors duration-75
                            "
                          >
                            <Icon size={15} className="flex-shrink-0" />
                            <span className="flex-1">{label}</span>
                            <kbd className="text-[10px] font-mono bg-surface-sunken px-1.5 py-0.5 rounded text-ink-muted">{shortcut}</kbd>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    </div>
                  </>
                )}

                {/* Conversation results (scope = global) */}
                {messagesSearchEnabled && messages.length > 0 && (
                  <Command.Group heading="Conversations" className="px-2">
                    {messages.map((message) => {
                      const result = formatMessageSearchResult(message);
                      return (
                        <Command.Item
                          key={message.id}
                          value={`message-${message.id}`}
                          onSelect={() => go(messageSearchHref(message))}
                          className="
                            flex items-start gap-3 px-3 py-2 rounded-lg
                            text-sm text-ink cursor-pointer
                            data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                            hover:bg-surface-sunken transition-colors duration-75
                          "
                        >
                          <MessageSquare size={15} className="mt-0.5 flex-shrink-0 text-primary" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate font-medium mask-sensitive">{result.title}</span>
                              {result.needsReview && (
                                <span className="rounded-full bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                                  Review
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-ink-muted truncate mask-sensitive">{result.subtitle}</div>
                            <div className="text-xs text-ink-subtle truncate mask-sensitive">{result.preview}</div>
                          </div>
                          <ArrowRight size={13} className="mt-1 text-ink-subtle flex-shrink-0" />
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                )}

                {/* Deal results (scope = deal | global) */}
                {dealsSearchEnabled && deals.length > 0 && (
                  <Command.Group heading="Deals" className="px-2">
                    {deals.map((deal) => (
                      <Command.Item
                        key={deal.id}
                        value={`deal-${deal.id}`}
                        onSelect={() => go(`/deals/${deal.id}`)}
                        className="
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm text-ink cursor-pointer
                          data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                          hover:bg-surface-sunken transition-colors duration-75
                        "
                      >
                        <FolderOpen size={15} className="flex-shrink-0 text-info" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium mask-sensitive">{dealDisplayTitle(deal)}</div>
                          <div className="text-xs text-ink-muted capitalize">
                            {dealStageSubtitle(deal)}
                          </div>
                        </div>
                        <ArrowRight size={13} className="text-ink-subtle flex-shrink-0" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Party/contact results (scope = party | global) */}
                {partiesSearchEnabled && parties.length > 0 && (
                  <Command.Group heading={objectType === 'contact' ? 'Contacts' : 'Parties'} className="px-2">
                    {parties.map((party) => (
                      <Command.Item
                        key={party.id}
                        value={`${objectType === 'contact' ? 'contact' : 'party'}-${party.id}`}
                        onSelect={() => go(`/parties/${party.id}`)}
                        className="
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm text-ink cursor-pointer
                          data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                          hover:bg-surface-sunken transition-colors duration-75
                        "
                      >
                        <AtSign size={15} className="flex-shrink-0 text-chart-4" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium mask-sensitive">{party.displayName}</div>
                          <div className="text-xs text-ink-muted truncate mask-sensitive">{party.email || party.phone || 'Party profile'}</div>
                        </div>
                        <ArrowRight size={13} className="text-ink-subtle flex-shrink-0" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Note results */}
                {notesSearchEnabled && notes.length > 0 && (
                  <Command.Group heading="Notes" className="px-2">
                    {notes.map((note) => (
                      <Command.Item
                        key={note.id}
                        value={`note-${note.id}`}
                        onSelect={() => go(note.dealThreadId ? `/deals/${note.dealThreadId}` : '/inbox')}
                        className="
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm text-ink cursor-pointer
                          data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                          hover:bg-surface-sunken transition-colors duration-75
                        "
                      >
                        <FileText size={15} className="flex-shrink-0 text-warning" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{noteTitle(note)}</div>
                          <div className="text-xs text-ink-muted truncate">{note.status} note</div>
                        </div>
                        <ArrowRight size={13} className="text-ink-subtle flex-shrink-0" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Task results */}
                {tasksSearchEnabled && tasks.length > 0 && (
                  <Command.Group heading="Tasks" className="px-2">
                    {tasks.map((task) => (
                      <Command.Item
                        key={task.id}
                        value={`task-${task.id}`}
                        onSelect={() => go('/planner')}
                        className="
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm text-ink cursor-pointer
                          data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                          hover:bg-surface-sunken transition-colors duration-75
                        "
                      >
                        <CheckSquare size={15} className="flex-shrink-0 text-primary" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium mask-sensitive">{task.title}</div>
                          <div className="text-xs text-ink-muted truncate mask-sensitive">{taskSubtitle(task)}</div>
                        </div>
                        <ArrowRight size={13} className="text-ink-subtle flex-shrink-0" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* AI Employee results */}
                {employeesSearchEnabled && employees.length > 0 && (
                  <Command.Group heading="AI Employees" className="px-2">
                    {employees.map((employee) => (
                      <Command.Item
                        key={employee.id}
                        value={`ai-employee-${employee.id}`}
                        onSelect={() => go('/ai-employees')}
                        className="
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          text-sm text-ink cursor-pointer
                          data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                          hover:bg-surface-sunken transition-colors duration-75
                        "
                      >
                        <Bot size={15} className="flex-shrink-0 text-primary" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{employee.name}</div>
                          <div className="text-xs text-ink-muted truncate">{employee.description || employee.status}</div>
                        </div>
                        <ArrowRight size={13} className="text-ink-subtle flex-shrink-0" />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Action scope - dispatches to Neo NL endpoint. Shows a
                    transient banner with the matched skill + auto-closes. */}
                {scope === 'action' && term.length >= 2 && !dispatching && (
                  <Command.Group heading="Actions" className="px-2">
                    <Command.Item
                      value="action-dispatch"
                      onSelect={() => dispatchNlAction(term, onClose)}
                      className="
                        flex items-center gap-3 px-3 py-2 rounded-lg
                        text-sm text-ink cursor-pointer
                        data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary
                        hover:bg-surface-sunken transition-colors duration-75
                      "
                    >
                      <Sparkles size={15} className="flex-shrink-0 text-primary" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Run: "{term}"</div>
                        <div className="text-xs text-ink-muted">Dispatch to Neo (eg. "send a follow-up to John", "summarize this thread")</div>
                      </div>
                    </Command.Item>
                  </Command.Group>
                )}

                {/* Dispatch result banner. Replaces the action picker while
                    the request is in flight + when the result arrives. */}
                {scope === 'action' && dispatching && (
                  <div className="px-3 py-4">
                    <div
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm ${
                        dispatching.state === 'pending'
                          ? 'bg-brand-subtle/30 border-brand/20 text-ink'
                          : dispatching.state === 'error'
                            ? 'bg-danger/10 border-danger/20 text-danger'
                            : 'bg-success/10 border-success/20 text-success'
                      }`}
                    >
                      <Sparkles size={15} className="flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        {dispatching.state === 'pending' && <span>Dispatching to Neo...</span>}
                        {dispatching.state !== 'pending' && (
                          <span>
                            {dispatching.source === 'stub' && <span className="opacity-70">[demo] </span>}
                            {dispatching.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(messagesSearchEnabled || dealsSearchEnabled || partiesSearchEnabled || notesSearchEnabled || tasksSearchEnabled || employeesSearchEnabled)
                  && messages.length === 0 && deals.length === 0 && parties.length === 0 && notes.length === 0 && tasks.length === 0 && employees.length === 0 && (
                  <Command.Empty className="py-8 text-center text-sm text-ink-subtle">
                    No results for "{term}"
                  </Command.Empty>
                )}
              </Command.List>

              {/* Footer hint */}
              <div className="px-4 py-2 border-t border-line bg-surface-sunken/50 flex items-center justify-between text-[10px] text-ink-subtle">
                <span>{SCOPE_LABEL[scope]}</span>
                <span className="flex items-center gap-2">
                  <kbd className="font-mono">↑↓</kbd> navigate
                  <kbd className="font-mono">↵</kbd> select
                  <kbd className="font-mono">esc</kbd> close
                </span>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
